import JSZip from 'jszip'
import { saveAs } from 'file-saver'

export type VideoExportItem = {
  url: string
  filename?: string
}

type SaveVideosOptions = {
  baseFilename?: string
  zipFilename?: string
}

const DEFAULT_VIDEO_BASENAME = 'Bazaart'
const DEFAULT_VIDEO_ZIP_FILENAME = 'bazaart.zip'

/**
 * Fetches a public video asset and returns it as a Blob for download packaging.
 */
export async function fetchVideoBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: 'cors' })
  if (!response.ok) throw new Error(`Video fetch failed: ${response.status}`)
  return response.blob()
}

export function saveBlobAs(blob: Blob, filename: string): void {
  saveAs(blob, filename)
}

function getVideoFilename(
  video: VideoExportItem,
  index: number,
  total: number,
  baseFilename: string,
): string {
  if (video.filename) return video.filename
  return total === 1 ? `${baseFilename}.mp4` : `${baseFilename}-${index + 1}.mp4`
}

/**
 * Saves one or more remote MP4 URLs. A single video downloads directly; multiple
 * videos are packaged into one ZIP archive.
 */
export async function saveVideos(
  videos: VideoExportItem[],
  options: SaveVideosOptions = {},
): Promise<void> {
  if (videos.length === 0) return

  const {
    baseFilename = DEFAULT_VIDEO_BASENAME,
    zipFilename = DEFAULT_VIDEO_ZIP_FILENAME,
  } = options

  if (videos.length === 1) {
    const video = videos[0]
    const blob = await fetchVideoBlob(video.url)
    saveBlobAs(blob, getVideoFilename(video, 0, 1, baseFilename))
    return
  }

  const zip = new JSZip()
  const fetched = await Promise.all(
    videos.map(async (video, index) => ({
      filename: getVideoFilename(video, index, videos.length, baseFilename),
      blob: await fetchVideoBlob(video.url),
    })),
  )

  for (const { filename, blob } of fetched) {
    zip.file(filename, blob)
  }

  saveBlobAs(await zip.generateAsync({ type: 'blob' }), zipFilename)
}

/**
 * Convenience helper for callers that only have video URLs and want default
 * Bazaart-style filenames.
 */
export async function exportVideos(
  urls: string[],
  options: SaveVideosOptions = {},
): Promise<void> {
  await saveVideos(urls.map(url => ({ url })), options)
}